import { FIRM_NAME_MAP } from '@app/layouts/UnauthorizedPageLayout/constants';

import { capitalize } from './capitalize';

interface QueryParamsType {
	branch?: string;
	branch_id?: string;
	branch_route?: string;
	rep_code?: string;
	rep_route?: string;
	auth_form_slug?: string;
}

const getAllQueryParams = () => {
	if (typeof window === 'undefined') return null;
	else {
		return Array.from(new URLSearchParams(window.location.search)).reduce(
			(acc, [key, value]) => ({ ...acc, [key]: value }),
			{} as QueryParamsType,
		);
	}
};

const getEnvFromQueryParams = ({ isDefault }: { isDefault?: boolean } = {}) => {
	const queryParams = getAllQueryParams();
	if (queryParams?.branch_id && queryParams?.auth_form_slug) {
		const branchRoute = {
			...queryParams,
			branch: queryParams.branch || queryParams.branch_id,
			branch_route: queryParams.branch_route || queryParams.branch_id,
			rep_route: queryParams.rep_route || queryParams.rep_code || 'url',
			is_branch_from_query_params: true,
		};
		return JSON.stringify(isDefault ? branchRoute : [branchRoute]);
	} else return (isDefault ? process.env.DEFAULT_BRANCH : process.env.BRANCH_ROUTES) as string;
};

export interface BranchEnvironmentVariable {
	branch: string;
	branch_id: number;
	branch_route: string;
	rep_code?: string;
	rep_route: string;
	auth_form_slug: string;
	is_branch_from_query_params?: boolean;
}

export interface EnvVariables {
	API_URL: string;
	BUILD_ENVIRONMENT: 'development' | 'production';
	FIRM_ENVIRONMENT: string;
	BRANCH_ROUTES: string;
	DEFAULT_BRANCH: string;
	ALLOW_USER_CREATION: string;
	ALLOW_APPLICATION_CREATION: string;
	ENABLE_SSO: string;
	SSO_ENVIRONMENT_URL: string;
	RETRIEVAL_ONLY: string;
	SUPPORT_ACCOUNT_RELATED: string;
	SUPPORT_FAX: string;
	SUPPORT_PHONE_NUMBER: string;
	SUPPORT_ADDRESS: string;
	RIA_APP_BUILD_NUMBER: string;
	TOP_LEVEL_DOMAIN: string;
	DEPLOYMENT_ENVIRONMENT: 'development' | 'production' | 'staging';
}

export const env: EnvVariables = {
	API_URL: process.env.API_URL as string,
	BUILD_ENVIRONMENT: process.env.BUILD_ENVIRONMENT as 'development' | 'production',
	DEPLOYMENT_ENVIRONMENT: process.env.DEPLOYMENT_ENVIRONMENT as 'development' | 'production' | 'staging',
	FIRM_ENVIRONMENT: process.env.FIRM_ENVIRONMENT as string,
	BRANCH_ROUTES: getEnvFromQueryParams(),
	DEFAULT_BRANCH: getEnvFromQueryParams({ isDefault: true }),
	ALLOW_USER_CREATION: process.env.ALLOW_USER_CREATION as string,
	ALLOW_APPLICATION_CREATION: process.env.ALLOW_APPLICATION_CREATION as string,
	ENABLE_SSO: process.env.ALLOW_APPLICATION_CREATION as string,
	SSO_ENVIRONMENT_URL: process.env.SSO_ENVIRONMENT_URL as string,
	RETRIEVAL_ONLY: process.env.RETRIEVAL_ONLY as string,
	SUPPORT_ACCOUNT_RELATED: process.env.SUPPORT_ACCOUNT_RELATED as string,
	SUPPORT_FAX: process.env.SUPPORT_FAX as string,
	SUPPORT_PHONE_NUMBER: process.env.SUPPORT_PHONE_NUMBER as string,
	SUPPORT_ADDRESS: process.env.SUPPORT_ADDRESS as string,
	RIA_APP_BUILD_NUMBER: process.env.RIA_APP_BUILD_NUMBER as string,
	TOP_LEVEL_DOMAIN: process.env.TOP_LEVEL_DOMAIN as 'US' | 'AU' | 'IN',
};

export const FALLBACK_BRANCH_ENVIRONMENT: BranchEnvironmentVariable = {
	branch: 'viewtrade-vtdt-retail',
	branch_id: 1,
	branch_route: 'vt',
	rep_route: 'retail',
	auth_form_slug: 'Test1',
	is_branch_from_query_params: false,
};

export const getDefaultBranch = (): BranchEnvironmentVariable => {
	if (!env.DEFAULT_BRANCH) {
		return FALLBACK_BRANCH_ENVIRONMENT;
	}

	try {
		const defaultBranch = JSON.parse(env.DEFAULT_BRANCH.replace(/\\/g, '') as string);
		return {
			branch: defaultBranch.branch,
			branch_id: Number(defaultBranch.branch_id),
			branch_route: defaultBranch.branch_route,
			rep_code: defaultBranch.rep_code,
			rep_route: defaultBranch.rep_route,
			auth_form_slug: defaultBranch.auth_form_slug,
			is_branch_from_query_params: Boolean(defaultBranch.is_branch_from_query_params),
		};
	} catch (e) {
		return FALLBACK_BRANCH_ENVIRONMENT;
	}
};

export const getBranchRoutes = (): BranchEnvironmentVariable[] => {
	if (!env.BRANCH_ROUTES) {
		return [getDefaultBranch()];
	}

	try {
		const branchRoutes = JSON.parse(env.BRANCH_ROUTES.replace(/\\/g, '') as string);
		return branchRoutes.map((branchRoute: BranchEnvironmentVariable) => ({
			branch: branchRoute.branch,
			branch_id: Number(branchRoute.branch_id),
			branch_route: branchRoute.branch_route,
			rep_code: branchRoute.rep_code,
			rep_route: branchRoute.rep_route,
			auth_form_slug: branchRoute.auth_form_slug,
			is_branch_from_query_params: Boolean(branchRoute.is_branch_from_query_params),
		}));
	} catch (e) {
		return [FALLBACK_BRANCH_ENVIRONMENT];
	}
};

export const isRetrievalOnly = (): boolean => {
	return process.env.RETRIEVAL_ONLY === 'true';
};

export const isAllowUserRegistration = (): boolean => {
	return process.env.ALLOW_USER_CREATION === 'true';
};

export const isAllowApplicationCreation = (): boolean => {
	return process.env.ALLOW_APPLICATION_CREATION === 'true';
};

export const isEnableSSO = (): boolean => {
	return process.env.ENABLE_SSO === 'true';
};

export const isDevelopment = (): boolean => {
	return process.env.BUILD_ENVIRONMENT === 'development';
};

export const isProduction = (): boolean => {
	return process.env.BUILD_ENVIRONMENT === 'production';
};

export const FIRM_NAME = FIRM_NAME_MAP.get(env.FIRM_ENVIRONMENT) || capitalize(env.FIRM_ENVIRONMENT);
